import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { getFromSchema } from '@soundxyz/utils';
import {
  ERROR_TYPE,
  MISCELLANEOUS_ERROR_ACTIONS,
  PILLARS,
} from '@soundxyz/utils/constants/logging';
import {
  BackendPersistedDataSchema,
  DefaultBackendPersistedDataSchema,
} from '@soundxyz/utils/constants/schemas/backendPersistedData';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { useAuth } from '@context/auth/base';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  SetUserPersistedDataDocument,
  useMutation,
  useQuery,
  UserBackendPersistedDataDocument,
} from '@graphql/reactQuery';
import { useLogError } from './useLogErrorNew';

/*#__PURE__*/ gql(/* GraphQL */ `
  query UserBackendPersistedData {
    getUserPersistedData
  }

  mutation SetUserPersistedData($data: JSON!) {
    setUserPersistedData(data: $data) {
      ... on MutationSetUserPersistedDataSuccess {
        data
      }

      ... on Error {
        message
      }
    }
  }
`);

RefetchOnComplete({
  trigger: [SetUserPersistedDataDocument],
  refetch: [UserBackendPersistedDataDocument],
});

export function useBackendUserPersistedData() {
  const { isAuthenticatedWithAPI, authUser } = useAuth();
  const {
    data,
    isLoading: loading,
    error,
  } = useQuery(UserBackendPersistedDataDocument, {
    staleTime: 0,
    filterQueryKey: [authUser?.id],
    enabled: isAuthenticatedWithAPI,
  });

  const logError = useLogError();
  const pillar = PILLARS.GLOBAL;

  const hasData = !!data;

  const persistedData = React.useMemo(() => {
    if (!data) return DefaultBackendPersistedDataSchema;

    const parsedData = BackendPersistedDataSchema.safeParse(data.data.getUserPersistedData);

    if (!parsedData.success) {
      logError({
        action: MISCELLANEOUS_ERROR_ACTIONS.USER_BACKEND_PERSISTED_DATA_FETCH_ERROR,
        error: new Error(parsedData.error.message),
        errorType: ERROR_TYPE.PARSE_ERROR,
        message: parsedData.error.message,
        level: 'error',
        isArtistOnlyPage: false,
        pillar,
      });
      return DefaultBackendPersistedDataSchema;
    }
    return parsedData.data;
  }, [data, logError, pillar]);

  const { mutate } = useMutation(SetUserPersistedDataDocument, {
    onError: err => {
      logError({
        action: MISCELLANEOUS_ERROR_ACTIONS.USER_BACKEND_PERSISTED_DATA_UPDATE_ERROR,
        error: new Error(err.message),
        errorType: ERROR_TYPE.USER_BACKEND_PERSISTED_DATA_UPDATE_ERROR,
        message: err.message,
        toast: 'There was an error updating your settings. Please try again.',
        level: 'warning',
        isArtistOnlyPage: false,
        pillar,
      });
    },
  });

  const setHasFeedAccess = useStableCallback((hasFeedAccess: boolean) => {
    const data = getFromSchema(BackendPersistedDataSchema, {
      ...persistedData,
      hasFeedAccess,
    });
    mutate({
      data,
    });
  });

  const setHasDismissedNewPostFeatureBanner = useStableCallback(
    (hasDismissedNewPostFeatureBanner: boolean) => {
      const data = getFromSchema(BackendPersistedDataSchema, {
        ...persistedData,
        hasDismissedNewPostFeatureBanner,
      });
      mutate({
        data,
      });
    },
  );

  const hasDismissedNewPostFeatureBanner = React.useMemo(() => {
    if (loading || !hasData)
      return {
        type: 'loading',
      } as const;
    if (!!persistedData.hasDismissedNewPostFeatureBanner) {
      return {
        type: 'dismissed',
      } as const;
    }
    return {
      type: 'not-dismissed',
    } as const;
  }, [hasData, loading, persistedData.hasDismissedNewPostFeatureBanner]);

  const hasFeedAccess = React.useMemo(() => {
    if (loading || !hasData)
      return {
        type: 'loading',
      } as const;
    if (!!persistedData.hasFeedAccess) {
      return {
        type: 'has-access',
      } as const;
    }
    return {
      type: 'no-access',
    } as const;
  }, [hasData, loading, persistedData.hasFeedAccess]);

  return {
    setHasFeedAccess,
    setHasDismissedNewPostFeatureBanner,
    hasFeedAccess,
    hasDismissedNewPostFeatureBanner,
    loading,
    error,
  };
}
